import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Plus,
  Trash2,
  Loader2,
  Building2,
  Users,
  Pencil,
  Key,
  Clock,
  Eye,
  EyeOff,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminLoginGate({ onAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user } = response.data;
      if (!user.is_superadmin) {
        setError('Access denied. Superadmin privileges required.');
        setLoading(false);
        return;
      }
      onAuthenticated(access_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
            <ShieldCheck className="w-6 h-6 text-purple-600" />
          </div>
          <CardTitle className="text-xl">Admin Login</CardTitle>
          <p className="text-gray-500 text-sm mt-1">Enter superadmin credentials to continue</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminPage() {
  const [adminToken, setAdminToken] = useState(sessionStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('tenants');
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Create an axios instance with admin token
  const adminApi = React.useMemo(() => {
    const instance = axios.create();
    if (adminToken) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    }
    return instance;
  }, [adminToken]);

  // Create tenant dialog
  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', scrini_api_key: '', allocated_minutes: '' });

  // Edit tenant dialog
  const [editTenantOpen, setEditTenantOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTenant, setEditTenant] = useState(null);

  // Create user dialog
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', tenant_id: '' });

  // API key visibility
  const [visibleKeys, setVisibleKeys] = useState({});

  useEffect(() => {
    if (adminToken) {
      fetchTenants();
      fetchUsers();
    }
  }, [adminToken]);

  const handleAdminAuth = (token) => {
    sessionStorage.setItem('admin_token', token);
    setAdminToken(token);
  };

  if (!adminToken) {
    return <AdminLoginGate onAuthenticated={handleAdminAuth} />;
  }

  const fetchTenants = async () => {
    try {
      const response = await adminApi.get(`${API}/admin/tenants`);
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to fetch tenants');
    } finally {
      setLoadingTenants(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminApi.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.name.trim()) {
      toast.error('Tenant name is required');
      return;
    }
    setCreating(true);
    try {
      await adminApi.post(`${API}/admin/tenants`, {
        name: newTenant.name,
        scrini_api_key: newTenant.scrini_api_key || null,
        allocated_minutes: newTenant.allocated_minutes ? parseInt(newTenant.allocated_minutes) : null
      });
      toast.success('Tenant created');
      setCreateTenantOpen(false);
      setNewTenant({ name: '', scrini_api_key: '', allocated_minutes: '' });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleEditTenant = async () => {
    if (!editTenant) return;
    setEditing(true);
    try {
      await adminApi.put(`${API}/admin/tenants/${editTenant.id}`, {
        name: editTenant.name,
        scrini_api_key: editTenant.scrini_api_key || null,
        allocated_minutes: editTenant.allocated_minutes !== '' && editTenant.allocated_minutes !== null
          ? parseInt(editTenant.allocated_minutes) : null
      });
      toast.success('Tenant updated');
      setEditTenantOpen(false);
      setEditTenant(null);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Delete this tenant? This will also delete all users and data.')) return;
    try {
      await adminApi.delete(`${API}/admin/tenants/${tenantId}`);
      toast.success('Tenant deleted');
      fetchTenants();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      toast.error('Email and password are required');
      return;
    }
    setCreatingUser(true);
    try {
      await adminApi.post(`${API}/admin/users`, {
        email: newUser.email,
        password: newUser.password,
        name: newUser.name || null,
        tenant_id: newUser.tenant_id || null,
        role: 'admin'
      });
      toast.success('User created');
      setCreateUserOpen(false);
      setNewUser({ email: '', password: '', name: '', tenant_id: '' });
      fetchUsers();
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await adminApi.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleKeyVisibility = (id) => {
    setVisibleKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const maskKey = (key) => {
    if (!key) return '-';
    if (key.length <= 8) return '****';
    return key.substring(0, 4) + '****' + key.substring(key.length - 4);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-500 mt-1">Manage tenants, users, API keys, and call minutes</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100">
            <TabsTrigger value="tenants" className="data-[state=active]:bg-blue-600">
              <Building2 className="w-4 h-4 mr-2" />
              Tenants
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-blue-600">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* ===== TENANTS TAB ===== */}
          <TabsContent value="tenants" className="mt-6">
            <Card className="border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Tenants</CardTitle>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setCreateTenantOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Tenant
                </Button>
              </CardHeader>
              <CardContent>
                {loadingTenants ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : tenants.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No tenants yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>API Key</TableHead>
                        <TableHead>Minutes (Used / Allocated)</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenants.map(tenant => (
                        <TableRow key={tenant.id}>
                          <TableCell className="font-medium">{tenant.name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                {visibleKeys[tenant.id] ? (tenant.scrini_api_key || '-') : maskKey(tenant.scrini_api_key)}
                              </code>
                              {tenant.scrini_api_key && (
                                <button onClick={() => toggleKeyVisibility(tenant.id)} className="text-gray-400 hover:text-gray-600">
                                  {visibleKeys[tenant.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              <span className={tenant.allocated_minutes && tenant.used_minutes >= tenant.allocated_minutes ? 'text-red-600 font-medium' : ''}>
                                {tenant.used_minutes}
                              </span>
                              <span className="text-gray-400">/</span>
                              <span>{tenant.allocated_minutes ?? 'Unlimited'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{tenant.user_count}</Badge>
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {format(new Date(tenant.created_at), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditTenant({
                                    ...tenant,
                                    allocated_minutes: tenant.allocated_minutes ?? ''
                                  });
                                  setEditTenantOpen(true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteTenant(tenant.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== USERS TAB ===== */}
          <TabsContent value="users" className="mt-6">
            <Card className="border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Users</CardTitle>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setCreateUserOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
              </CardHeader>
              <CardContent>
                {loadingUsers ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : users.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No users yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Tenant</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(u => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">
                            {u.name || '-'}
                            {u.is_superadmin && (
                              <Badge className="ml-2 bg-purple-100 text-purple-700 border-purple-200">Super Admin</Badge>
                            )}
                          </TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{u.tenant_name || '-'}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {format(new Date(u.created_at), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell className="text-right">
                            {!u.is_superadmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteUser(u.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ===== CREATE TENANT DIALOG ===== */}
        <Dialog open={createTenantOpen} onOpenChange={setCreateTenantOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Create Tenant</DialogTitle>
              <DialogDescription>Add a new tenant organization</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tenant Name</Label>
                <Input
                  placeholder="Acme Corp"
                  value={newTenant.name}
                  onChange={e => setNewTenant({ ...newTenant, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Scrini API Key
                  </div>
                </Label>
                <Input
                  placeholder="dgr_xxxx..."
                  value={newTenant.scrini_api_key}
                  onChange={e => setNewTenant({ ...newTenant, scrini_api_key: e.target.value })}
                />
                <p className="text-xs text-gray-500">Leave empty to use the default global key</p>
              </div>
              <div className="space-y-2">
                <Label>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Allocated Minutes
                  </div>
                </Label>
                <Input
                  type="number"
                  placeholder="e.g. 500 (leave empty for unlimited)"
                  value={newTenant.allocated_minutes}
                  onChange={e => setNewTenant({ ...newTenant, allocated_minutes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateTenantOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreateTenant} disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== EDIT TENANT DIALOG ===== */}
        <Dialog open={editTenantOpen} onOpenChange={setEditTenantOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Edit Tenant</DialogTitle>
              <DialogDescription>Update tenant settings</DialogDescription>
            </DialogHeader>
            {editTenant && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Tenant Name</Label>
                  <Input
                    value={editTenant.name}
                    onChange={e => setEditTenant({ ...editTenant, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    <div className="flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Scrini API Key
                    </div>
                  </Label>
                  <Input
                    placeholder="dgr_xxxx..."
                    value={editTenant.scrini_api_key || ''}
                    onChange={e => setEditTenant({ ...editTenant, scrini_api_key: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">Leave empty to use the default global key</p>
                </div>
                <div className="space-y-2">
                  <Label>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Allocated Minutes
                    </div>
                  </Label>
                  <Input
                    type="number"
                    placeholder="Leave empty for unlimited"
                    value={editTenant.allocated_minutes}
                    onChange={e => setEditTenant({ ...editTenant, allocated_minutes: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    Currently used: <span className="font-medium">{editTenant.used_minutes} min</span>
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditTenantOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleEditTenant} disabled={editing}>
                {editing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== CREATE USER DIALOG ===== */}
        <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Create User</DialogTitle>
              <DialogDescription>Add a new user to a tenant</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  placeholder="John Doe"
                  value={newUser.name}
                  onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Assign to Tenant</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  value={newUser.tenant_id}
                  onChange={e => setNewUser({ ...newUser, tenant_id: e.target.value })}
                >
                  <option value="">-- Create new tenant --</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateUserOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreateUser} disabled={creatingUser}>
                {creatingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
